import _get from "lodash/get";
import React, { useEffect, useState, useContext } from 'react';
import { rem } from 'polished';
import { injectIntl } from 'react-intl';
import { ThemeProvider } from 'styled-components';
import { animated, useTransition } from 'react-spring';
import { ThemeModeContext } from '~theme-provider';
import ModalOverlay from './ModalOverlay';
import ModalContent from './ModalContent';
import ModalHeader from './ModalHeader';
import ModalBody from './ModalBody';
import ModalFooter from './ModalFooter';
import ModalCloseButton from './ModalCloseButton';
import ModalCloseIcon from './ModalCloseButton/ModalCloseIcon';
import modalPropTypes from '../../prop-types/modalPropTypes';
import modalTheme from '../../styles/modalTheme';
import modalDarkTheme from '../../styles/modalDarkTheme';

var Modal = function Modal(_ref) {
  var intl = _ref.intl,
      children = _ref.children,
      isContentCover = _ref.isContentCover,
      title = _ref.title,
      titleAriaLabel = _ref.titleAriaLabel,
      customHeader = _ref.customHeader,
      footerActions = _ref.footerActions,
      footerPre = _ref.footerPre,
      footerPost = _ref.footerPost,
      customFooter = _ref.customFooter,
      isOpen = _ref.isOpen,
      onDismiss = _ref.onDismiss,
      closeIcon = _ref.closeIcon,
      closeIconColor = _ref.closeIconColor,
      showCloseButton = _ref.showCloseButton,
      size = _ref.size,
      isDismissible = _ref.isDismissible,
      entryTransitionY = _ref.entryTransitionY,
      isOverlayTransparent = _ref.isOverlayTransparent,
      offsetTop = _ref.offsetTop;

  var _useContext = useContext(ThemeModeContext),
      themeMode = _useContext.themeMode;

  var _useState = useState(isOpen),
      showDialog = _useState[0],
      setShowDialog = _useState[1];

  var _useState2 = useState(isOpen),
      initialIsOpen = _useState2[0],
      setInitialIsOpen = _useState2[1];

  var theme = useContext(ThemeProvider);

  var handleOnDismiss = function handleOnDismiss() {
    setShowDialog(!isDismissible);
  };

  var transitionY = _get(theme, 'modal.transition.y', modalTheme.modal.transition.y);

  var defaultCloseIconColor = themeMode === 'darkMode' ? _get(theme, 'modal.closeIcon.color', modalDarkTheme.modal.closeIcon.color) : _get(theme, 'modal.closeIcon.color', modalTheme.modal.closeIcon.color);
  var transition = useTransition(showDialog, {
    from: {
      opacity: 0,
      y: Number(entryTransitionY) || transitionY
    },
    enter: {
      opacity: 1,
      y: 0
    },
    leave: {
      opacity: 0,
      y: Number(entryTransitionY) || transitionY
    },
    onDestroyed: function onDestroyed() {
      onDismiss();
      setInitialIsOpen(false);
    }
  });
  useEffect(function () {
    setShowDialog(isOpen);
  }, [isOpen]);
  var renderCloseIcon = closeIcon || /*#__PURE__*/React.createElement(ModalCloseIcon, {
    color: closeIconColor || defaultCloseIconColor
  });
  var AnimatedContainer = animated(ModalOverlay);
  var AnimatedContentContainer = animated(ModalContent);
  var hasDefaultFooter = (footerActions || footerPre || footerPost) && !customFooter;
  return /*#__PURE__*/React.createElement(React.Fragment, null, transition(function (_ref2, item) {
    var opacity = _ref2.opacity,
        y = _ref2.y;
    return item && /*#__PURE__*/React.createElement(AnimatedContainer, {
      isOpen: initialIsOpen,
      style: {
        opacity: opacity
      },
      onDismiss: handleOnDismiss,
      $isOverlayTransparent: isOverlayTransparent
    }, /*#__PURE__*/React.createElement(AnimatedContentContainer, {
      $offsetTop: offsetTop,
      "aria-label": titleAriaLabel || title,
      style: {
        transform: y.to(function (value) {
          return "translate3d(0, " + rem(value) + ", 0)";
        })
      },
      $modalSize: size,
      $isOverlayTransparent: isOverlayTransparent
    }, showCloseButton && /*#__PURE__*/React.createElement(ModalCloseButton, {
      "aria-label": intl.formatMessage({
        id: 'modal.close.label'
      }),
      onClick: handleOnDismiss
    }, renderCloseIcon), title && !customHeader && /*#__PURE__*/React.createElement(ModalHeader, {
      title: title
    }), customHeader && customHeader, /*#__PURE__*/React.createElement(ModalBody, {
      $isContentCover: isContentCover,
      hasFooter: hasDefaultFooter
    }, children), hasDefaultFooter && /*#__PURE__*/React.createElement(ModalFooter, {
      footerActions: footerActions,
      footerPre: footerPre,
      footerPost: footerPost,
      handleOnDismiss: handleOnDismiss
    }), customFooter && customFooter));
  }));
};

Modal.defaultProps = {
  children: undefined,
  isContentCover: false,
  titleAriaLabel: undefined,
  isOpen: false,
  onDismiss: function onDismiss() {},
  size: 'default',
  customHeader: undefined,
  footerActions: undefined,
  footerPre: undefined,
  footerPost: undefined,
  customFooter: undefined,
  closeIcon: undefined,
  closeIconColor: undefined,
  showCloseButton: true,
  isDismissible: true,
  entryTransitionY: undefined,
  isOverlayTransparent: false,
  offsetTop: undefined
};
export default injectIntl(Modal);